<template>
  <div class="xg-video" :class="{ 'xg-video--webrtc': decodeMode === 'webrtc' }">
    <!-- FLV：xgplayer-flv -->
    <div v-if="decodeMode === 'flv'" class="video" v-show="isPlay" :id="playerId" />
    <!-- RTSP：经 webrtc-streamer 等转为 WebRTC，由原生 video 播放 -->
    <video
      v-if="decodeMode === 'webrtc'"
      :id="rtcVideoElementId"
      ref="rtcVideo"
      class="video rtc-video"
      v-show="isPlay"
      autoplay
      playsinline
      muted
    />
    <div v-show="!isPlay" class="no-video" :class="{ 'no-video--tip': !!idleMessage }">
      {{ idleMessage || '暂无视频源' }}
    </div>
  </div>

</template>
<script>
import FlvJsPlayer from 'xgplayer-flv.js';
import Player from 'xgplayer';
import 'xgplayer/dist/index.min.css';

function normalizeHttpBase(url) {
  let u = (url || '').trim().replace(/\/$/, '');
  if (!u) return '';
  if (!/^https?:\/\//i.test(u)) {
    u = `http://${u}`;
  }
  return u;
}

export default {
  name: 'XgPlayer',
  data() {
    return {
      player: null,
      isPlay: false,
      retryNumber: 0,
      lastDecodedFrame: 0,
      syncProgressTimer: null,
      idleMessage: '',
      /** flv | webrtc | none */
      decodeMode: 'flv',
      rtcStreamer: null,
    };
  },
  props: {
    playerId: {
      type: String,
      required: true,
      default: 'playerId',
    },
    videoPlayUrl: {
      type: String,
      required: true,
    },
    /**
     * webrtc-streamer 根地址（如 http://192.168.1.10:8000），用于浏览器内播放 RTSP。
     * 也可在环境变量中配置 VUE_APP_WEBRTC_STREAMER_BASE。
     */
    rtcSignalingBaseUrl: {
      type: String,
      default: '',
    },
    /** 覆盖默认的 webrtcstreamer.js 地址，默认 `${normalizeHttpBase(rtc)}/webrtcstreamer.js` */
    webrtcScriptUrl: {
      type: String,
      default: '',
    },
  },
  computed: {
    effectiveRtcBase() {
      const p = (this.rtcSignalingBaseUrl || '').trim();
      if (p) return p;
      const env = process.env.VUE_APP_WEBRTC_STREAMER_BASE || '';
      return (env || '').trim();
    },
    rtcVideoElementId() {
      return `${this.playerId}-rtc`;
    },
  },
  watch: {
    videoPlayUrl: {
      handler(val) {
        this.retryNumber = 0;
        if (val) {
          this.idleMessage = '';
          this.resetPlayer();
        } else {
          this.idleMessage = '';
          this.isPlay = false;
          this.decodeMode = 'flv';
          this.destroyRtcStreamer();
          if (this.player && typeof this.player.destroy === 'function') {
            try {
              this.player.destroy(true);
            } catch (e) {
              /* ignore */
            }
            this.player = null;
          }
        }
      },
      immediate: true,
    },
    $route() {
      this.destroyRtcStreamer();
      if (this.player) {
        this.player.pause();
        this.player.destroy(true);
        this.player = null;
      } else {
        this.retryNumber = 0;
        this.resetPlayer();
      }
    },
  },

  methods: {
    destroyRtcStreamer() {
      if (this.rtcStreamer && typeof this.rtcStreamer.disconnect === 'function') {
        try {
          this.rtcStreamer.disconnect();
        } catch (e) {
          /* ignore */
        }
      }
      this.rtcStreamer = null;
    },

    loadWebRtcStreamerScript(scriptUrl) {
      return new Promise((resolve, reject) => {
        if (typeof window.WebRtcStreamer === 'function') {
          resolve();
          return;
        }
        const marker = 'webrtcstreamer-lib-global';
        const existing = document.getElementById(marker);
        if (existing) {
          const t0 = Date.now();
          const wait = () => {
            if (typeof window.WebRtcStreamer === 'function') {
              resolve();
              return;
            }
            if (Date.now() - t0 > 15000) {
              reject(new Error('等待 WebRtcStreamer 超时'));
              return;
            }
            setTimeout(wait, 50);
          };
          wait();
          return;
        }
        const s = document.createElement('script');
        s.id = marker;
        s.async = true;
        s.src = scriptUrl;
        s.onload = () => {
          if (typeof window.WebRtcStreamer === 'function') resolve();
          else reject(new Error('脚本已加载但未注册 WebRtcStreamer'));
        };
        s.onerror = () => reject(new Error(`无法加载 ${scriptUrl}`));
        document.head.appendChild(s);
      });
    },

    async createRtspWebRtc(rawUrl) {
      this.destroyRtcStreamer();
      const base = this.effectiveRtcBase;
      if (!base) {
        this.decodeMode = 'none';
        this.isPlay = false;
        this.idleMessage =
          '当前为 RTSP 地址。浏览器无法直接拉 RTSP，需部署 webrtc-streamer（或 go2rtc 等）将流转为 WebRTC。请在「环境变量」中配置 VUE_APP_WEBRTC_STREAMER_BASE=webrtc-streamer 的根地址（如 http://192.168.1.10:8000），或给组件传入 rtc-signaling-base-url。部署说明：https://github.com/mpromonet/webrtc-streamer';
        return;
      }
      const httpBase = normalizeHttpBase(base);
      const scriptUrl =
        (this.webrtcScriptUrl || '').trim() || `${httpBase}/webrtcstreamer.js`;
      this.decodeMode = 'webrtc';
      this.idleMessage = '';
      try {
        await this.loadWebRtcStreamerScript(scriptUrl);
      } catch (e) {
        this.decodeMode = 'none';
        this.isPlay = false;
        this.idleMessage =
          (e && e.message) ||
          '无法加载 webrtcstreamer.js，请检查 rtcSignalingBaseUrl / VUE_APP_WEBRTC_STREAMER_BASE 与脚本路径（默认同源 /webrtcstreamer.js）。';
        return;
      }
      await this.$nextTick();
      const Ctor = window.WebRtcStreamer;
      if (typeof Ctor !== 'function') {
        this.decodeMode = 'none';
        this.isPlay = false;
        this.idleMessage = 'WebRtcStreamer 未就绪，请确认 webrtc-streamer 版本与脚本路径。';
        return;
      }
      try {
        this.rtcStreamer = new Ctor(this.rtcVideoElementId, httpBase);
        this.rtcStreamer.connect(rawUrl);
        this.isPlay = true;
        const el = this.$refs.rtcVideo;
        if (el && el.addEventListener) {
          el.addEventListener(
            'error',
            () => {
              this.idleMessage = 'WebRTC 播放失败，请检查 webrtc-streamer 与摄像头网络、账号密码及 RTSP 地址是否可达。';
              this.isPlay = false;
            },
            { once: true }
          );
        }
      } catch (e) {
        this.decodeMode = 'none';
        this.isPlay = false;
        this.idleMessage = (e && e.message) || 'WebRTC 初始化失败';
        this.rtcStreamer = null;
      }
    },

    resetPlayer() {
      this.destroyRtcStreamer();
      if (this.player && typeof this.player.destroy === 'function') {
        this.player.pause && this.player.pause();
        this.player.unload && this.player.unload();
        this.player.detachMediaElement && this.player.detachMediaElement();
        this.player.destroy(true);
        this.player = null;
      }
      setTimeout(() => {
        this.createPlayer(false, 0);
      }, 10);
    },

    createPlayer(hasCloseBtn) {
      if (!this.videoPlayUrl) {
        this.idleMessage = '';
        return;
      }
      const rawUrl = String(this.videoPlayUrl).trim();
      const low = rawUrl.toLowerCase();
      if (low.startsWith('rtsp://') || low.startsWith('rtsps://')) {
        this.decodeMode = 'webrtc';
        this.$nextTick(() => {
          this.createRtspWebRtc(rawUrl);
        });
        return;
      }
      this.destroyRtcStreamer();
      this.decodeMode = 'flv';
      this.idleMessage = '';
      this.isPlay = true;
      console.log('创建播放器实例(FLV)');
      this.player = new FlvJsPlayer({
        id: this.playerId,
        url: rawUrl,
        fluid: true,
        autoplay: true,
        isLive: true,
        playsinline: false,
        screenShot: true,
        whitelist: [''],
        ignores: ['time'],
        volume: 0,
        closeVideoClick: true,
        customConfig: {
          isClickPlayBack: false,
        },
        flvOptionalConfig: {
          enableWorker: true,
          enableStashBuffer: true,
          stashInitialSize: 4096,
          lazyLoad: false,
          lazyLoadMaxDuration: 40 * 60,
          autoCleanupSourceBuffer: true,
          autoCleanupMaxBackwardDuration: 35 * 60,
          autoCleanupMinBackwardDuration: 30 * 60,
        },
      });

      let divStr =
        '<i class="btn-hover  button-playback" style="font-size: 23px;pointer-events: auto;"></i>';
      let divClose = '<i @click="closePlayer" class="btn-hover el-icon-close app-close-btn-c"></i>';

      let util = Player.util;
      let customBtn = util.createDom(
        'div',
        divStr,
        { style: 'width: 40px;heigth:40px;position: absolute;right: 155px;top: 7px;' },
        'flex align-center justify-center app-player-button'
      );
      let closeBtn = util.createDom('div', divClose, {}, 'app-close-btn');
      if (!this.player || !this.player.root) {
        this.isPlay = false;
        this.idleMessage = '播放器初始化失败，请确认地址格式与网络环境。';
        this.player = null;
        return;
      }
      const xgControls = this.player.root.querySelector('xg-controls');
      if (xgControls) {
        xgControls.appendChild(customBtn);
      } else {
        this.player.root.appendChild(customBtn);
      }
      this.player.root.appendChild(closeBtn);

      let closeBtnc = closeBtn.querySelector('.app-close-btn-c');

      this.player.on('play', () => {});

      this.player.on('focus', () => {
        if (hasCloseBtn) {
          closeBtn.style.display = 'block';
        }
      });

      this.player.on('ended', () => {});

      this.player.on('blur', () => {
        closeBtn.style.display = 'none';
      });

      this.player.on('error', (errorType, errorDetail, errorInfo) => {
        console.log('errorType:', errorType);
        console.log('errorDetail:', errorDetail);
        console.log('errorInfo:', errorInfo);
        this.playError();
      });

      if (closeBtnc) {
        closeBtnc.addEventListener('click', () => {
          this.closePlayer();
        });
      }

      this.player.on('statistics_info', (res) => {
        if (!this.player) return;
        if (this.lastDecodedFrame === 0) {
          this.lastDecodedFrame = res.decodedFrames;
          return;
        }
        if (this.lastDecodedFrame !== res.decodedFrames) {
          this.lastDecodedFrame = res.decodedFrames;
        } else {
          this.lastDecodedFrame = 0;
          if (this.player) {
            this.resetPlayer();
          }
        }
      });
    },

    changeVideo(url) {
      if (this.player && this.player.src !== undefined) {
        this.player.src = url;
      }
    },

    closePlayer() {
      this.isPlay = false;
      this.destroy();
    },

    playError() {
      if (this.retryNumber > 4) {
        this.isPlay = false;
        this.idleMessage =
          '多次重连失败，请检查码流地址（HTTP-FLV / HLS）或 webrtc-streamer 与 RTSP 源、网络与鉴权是否正常。';
        return;
      }
      this.retryNumber++;
      this.resetPlayer();
    },

    destroy() {
      if (this.syncProgressTimer) {
        clearTimeout(this.syncProgressTimer);
        this.syncProgressTimer = null;
      }
      this.destroyRtcStreamer();
      if (this.player && typeof this.player.destroy === 'function') {
        try {
          this.player.destroy(true);
        } catch (e) {
          console.warn('XgPlayer destroy', e);
        }
        this.player = null;
      }
    },

    handleVisibilityChange() {
      if (document.visibilityState === 'visible') {
        this.resetPlayer();
      } else {
        this.destroyRtcStreamer();
        if (this.player) {
          this.player.pause();
          this.player.destroy(true);
          this.player = null;
        }
      }
    },

    syncPlayProgress() {
      if (this.syncProgressTimer) {
        clearTimeout(this.syncProgressTimer);
        this.syncProgressTimer = null;
      }
      if (!this.player || typeof this.player.buffered === 'undefined') {
        return;
      }
      try {
        if (this.player.buffered && this.player.buffered.length) {
          const end = this.player.buffered.end(0);
          const diff = end - this.player.currentTime;
          if (diff >= 3) {
            console.log('差值大于等于3 手动跳帧');
            this.player.currentTime = this.player.buffered.end(0);
          }
        }
      } catch (e) {
        /* ignore */
      }
      this.syncProgressTimer = setTimeout(() => {
        this.syncProgressTimer = null;
        this.syncPlayProgress();
      }, 5000);
    },
  },

  mounted() {
    window.addEventListener('visibilitychange', () => this.handleVisibilityChange());
    this.syncPlayProgress();
  },
  beforeDestroy() {
    this.destroy();
  },

  destroyed() {},
};
</script>
<style lang="scss" scoped>
.xg-video {
  border: 1px solid #55557f;
  position: relative;
}
.xg-video--webrtc {
  background: #000;
}
.xg-video /deep/ .app-close-btn {
  position: absolute;
  top: 0;
  right: 5px;
  z-index: 500;
  display: none;
  cursor: pointer;
}
.xg-video /deep/.app-close-btn-c {
  color: #aff;
  font-size: 25px;
  pointer-events: auto;
  z-index: 500;
}

.xg-video /deep/ .xgplayer-enter-spinner {
  width: 50% !important;
  height: 50% !important;
}

.video.rtc-video {
  width: 100%;
  height: 100%;
  min-height: 120px;
  object-fit: contain;
  display: block;
  background: #000;
}

.no-video {
  color: #8ae8eb;
  font-size: 20px;
  height: 100%;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 25% 0;
  text-align: center;
  box-sizing: border-box;
}
.no-video--tip {
  font-size: 13px;
  line-height: 1.55;
  color: #c5d0e0;
  padding: 12px 14px;
  max-width: 100%;
}
</style>
